import config from '@/config';

const SESSION_STORAGE_KEY = 'home_carousel_analytics_session_id';
const MAX_BATCH_SIZE = 30;
const FLUSH_INTERVAL_MS = 60 * 1000;
const ENDPOINT = '/home-carousel-analytics/events/batch';

const EVENT_NAMES = new Set([
  'home_view',
  'carousel_impression',
  'carousel_item_click',
  'carousel_see_all_click',
]);

let queue = [];
let flushTimer = null;
let isFlushing = false;
let listenersBound = false;

const isBrowser = typeof window !== 'undefined';

function normalizeString(value, maxLength = 180) {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  return normalized.slice(0, maxLength);
}

function normalizeEventName(eventName) {
  const normalized = normalizeString(eventName, 80)?.toLowerCase();
  if (!normalized || !EVENT_NAMES.has(normalized)) {
    return null;
  }
  return normalized;
}

function normalizePosition(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function inferDevice(payload = {}) {
  const explicit = normalizeString(payload.device, 20)?.toLowerCase();
  if (explicit === 'mobile' || explicit === 'desktop' || explicit === 'unknown') {
    return explicit;
  }

  if (typeof payload.isMobile === 'boolean') {
    return payload.isMobile ? 'mobile' : 'desktop';
  }

  if (!isBrowser) {
    return 'unknown';
  }

  return window.innerWidth <= 550 ? 'mobile' : 'desktop';
}

function buildSessionId() {
  if (!isBrowser) {
    return null;
  }

  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function getSessionId() {
  if (!isBrowser) {
    return null;
  }

  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing && typeof existing === 'string') {
      return existing;
    }

    const created = buildSessionId();
    if (!created) {
      return null;
    }

    window.sessionStorage.setItem(SESSION_STORAGE_KEY, created);
    return created;
  } catch (_error) {
    return null;
  }
}

function sanitizeMetadata(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return undefined;
  }

  const maxEntries = 20;
  const result = {};

  Object.entries(input)
    .slice(0, maxEntries)
    .forEach(([key, value]) => {
      const normalizedKey = normalizeString(String(key), 60);
      if (!normalizedKey) {
        return;
      }

      if (value === null) {
        result[normalizedKey] = null;
        return;
      }

      if (typeof value === 'string') {
        result[normalizedKey] = value.slice(0, 500);
        return;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        result[normalizedKey] = value;
      }
    });

  return Object.keys(result).length ? result : undefined;
}

function buildEventPayload(eventName, payload = {}, options = {}) {
  const normalizedEventName = normalizeEventName(eventName);
  if (!normalizedEventName) {
    return null;
  }

  const sessionId = normalizeString(payload.sessionId, 120) || getSessionId();
  if (!sessionId) {
    return null;
  }

  const occurredAt = payload.occurredAt ? new Date(payload.occurredAt) : new Date();
  const occurredAtValue = Number.isNaN(occurredAt.getTime())
    ? new Date().toISOString()
    : occurredAt.toISOString();

  const userSession = options.userSession || null;

  return {
    eventName: normalizedEventName,
    occurredAt: occurredAtValue,
    sessionId,
    page: normalizeString(payload.page, 80) || 'home_discover',
    collectionId: normalizeString(payload.collectionId, 120),
    collectionName: normalizeString(payload.collectionName, 140),
    carouselType: normalizeString(payload.carouselType, 80),
    contentMode: normalizeString(payload.contentMode, 40),
    position: normalizePosition(payload.position),
    itemId: normalizeString(payload.itemId, 140),
    itemKind: normalizeString(payload.itemKind, 80),
    destination: normalizeString(payload.destination, 250),
    device: inferDevice(payload),
    isAuthenticated: Boolean(userSession?._id || userSession?.user_id),
    userId: normalizeString(userSession?._id || userSession?.user_id, 120),
    userType: normalizeString(userSession?.user_type || userSession?.__t, 80),
    metadata: sanitizeMetadata(payload.metadata),
  };
}

function clearFlushTimer() {
  if (!flushTimer) {
    return;
  }

  window.clearTimeout(flushTimer);
  flushTimer = null;
}

async function sendBatch(events, useBeacon = false) {
  const url = `${config.apis.api.url}${ENDPOINT}`;
  const body = JSON.stringify({ events });

  if (useBeacon && isBrowser && typeof navigator.sendBeacon === 'function') {
    const payload = new Blob([body], { type: 'application/json' });
    const sent = navigator.sendBeacon(url, payload);
    if (sent) {
      return true;
    }
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body,
    keepalive: useBeacon,
  });

  return response.ok;
}

async function flushQueue({ useBeacon = false } = {}) {
  if (!isBrowser || isFlushing || queue.length === 0) {
    return;
  }

  isFlushing = true;
  clearFlushTimer();

  const pending = queue.slice();
  queue = [];

  try {
    const delivered = await sendBatch(pending, useBeacon);
    if (!delivered) {
      queue = [...pending, ...queue].slice(0, 300);
    }
  } catch (_error) {
    queue = [...pending, ...queue].slice(0, 300);
  } finally {
    isFlushing = false;

    if (queue.length > 0) {
      scheduleFlush();
    }
  }
}

function scheduleFlush() {
  if (!isBrowser || flushTimer || queue.length === 0) {
    return;
  }

  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, FLUSH_INTERVAL_MS);
}

function bindLifecycleListeners() {
  if (!isBrowser || listenersBound) {
    return;
  }

  listenersBound = true;

  const flushOnHide = () => {
    flushQueue({ useBeacon: true });
  };

  window.addEventListener('pagehide', flushOnHide);
  window.addEventListener('beforeunload', flushOnHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushOnHide();
    }
  });
}

function trackHomeCarouselEvent(eventName, payload = {}, options = {}) {
  if (!isBrowser) {
    return;
  }

  bindLifecycleListeners();

  const eventPayload = buildEventPayload(eventName, payload, options);
  if (!eventPayload) {
    return;
  }

  queue.push(eventPayload);

  if (queue.length >= MAX_BATCH_SIZE) {
    flushQueue();
    return;
  }

  scheduleFlush();
}

function trackHomeDiscoverView({ isMobile } = {}, options = {}) {
  trackHomeCarouselEvent('home_view', {
    collectionId: '_home',
    collectionName: 'Home Discover',
    carouselType: 'home',
    contentMode: 'system',
    position: 0,
    isMobile,
  }, options);
}

const HomeCarouselAnalyticsService = {
  trackHomeCarouselEvent,
  trackHomeDiscoverView,
  flushQueue,
};

export default HomeCarouselAnalyticsService;
